import React, { useEffect, useState } from "react";
import { Translations } from "../../../models/translations";
import { FormikFieldProps, showApiError } from "../../framework/formUtils";
import { AutocompleteOption, AutocompleteValue, AutocompleteWrapper } from "../../framework/muiAutocomplete";
import { getCustomerList } from "../../../services/customerService";

interface CustomerSelectProps extends Partial<FormikFieldProps> {
    value: string;
    small?: boolean;
    required?: boolean;
    label?: string;
    fullWidth?: boolean;
    disabled?: boolean;
    defaultOpen?: boolean;
    onChange?: (val: string) => void;
    // Optional: label for the currently selected value when it's not in the fetched options (e.g., inactive customer)
    selectedLabel?: string;
}

export const CustomerSelect = (props: CustomerSelectProps) => {
    const [customerOptions, setCustomerOptions] = useState<AutocompleteOption[]>([]);
    const [loading, setLoading] = useState(false);
    const { value, small, onChange, defaultOpen, selectedLabel, ...rest } = props;

    useEffect(() => {
        let customersFetched = false;
        const getCustomers = async() => {
            await getCustomerList()
                .then((res) => {
                    const customers = res?.data
                        .map(v => ({ id: v.id, title: v?.name }))
                        .sort((a, b) => a.title > b.title ? 1 : -1);
                    setCustomerOptions(customers);
                    setLoading(false);
                    customersFetched = true;
                })
                .catch(() => showApiError(Translations.FetchFailed));
        };
        if (customerOptions?.length === 0 && !customersFetched) {
            setLoading(true);
            getCustomers().catch(() => showApiError(Translations.FetchFailed));
        }
    }, [customerOptions]);

    const handleChange = (val: AutocompleteValue | null) => {
        if (val === null) {
            onChange?.(null);
        }
        else if ("id" in val) {
            onChange?.(val?.id);
        }
    };

    // Ensure current selection is visible by injecting a derived option when it doesn't exist in fetched options
    const mergedOptions: AutocompleteOption[] = React.useMemo(() => {
        if (value && selectedLabel && !customerOptions.some((o) => o.id === value)) {
            return [{ id: value, title: selectedLabel }, ...customerOptions];
        }
        return customerOptions;
    }, [customerOptions, selectedLabel, value]);

    return (
        <AutocompleteWrapper
            label={props.label ?? Translations.Customer}
            value={value}
            size={small ? "small" : "medium"}
            options={mergedOptions}
            onChange={handleChange}
            loading={loading}
            required={props.required}
            textFieldProps={{ ...rest }}
            fullWidth={props.fullWidth}
            disabled={props.disabled}
            defaultOpen={defaultOpen}
        />
    );
};
